import {
  ProductListConfiguration,
  ProductListConfigurationKeys,
  ProductListConfigurations,
  PRODUCT_LIST_NAMESPACE,
} from '@epicuro-next/constants/rule-engine/product-list-rules';
import { MediaType } from '@epicuro-next/models/engagement';
import {
  getValueFromConfigState,
  selectConfigurationsState,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import { selectListEntitySearchSortType } from '@epicuro-next/state/list-entities';
import {
  selectIsSalesRepOrAdmin,
  selectUser,
} from '@epicuro-next/state/user-state';
import { createSelector } from '@ngrx/store';

import {
  adapter,
  epicShowProductReferencesAdapter,
  epicShowProductsAdapter,
  productListOpenedFeatureKey,
  State,
} from '../../reducers/list-opened/product-list-opened.reducer';
import * as fromProductList from '../../store';

/**
 * state slice selector
 */
export const selectOpenedListState = createSelector<
  fromProductList.State,
  [fromProductList.ProductListState],
  State
>(
  fromProductList.selectProductListState,
  (state) => state[productListOpenedFeatureKey],
);

/**
 * state slice selector
 */
export const selectOpenedListProductsState = createSelector(
  selectOpenedListState,
  (state) => state.products,
);

const { selectAll } = adapter.getSelectors(selectOpenedListProductsState);

export const selectOpenedListProducts = selectAll;

export const selectEpicShowProductsState = createSelector(
  selectOpenedListState,
  (state) => state.epicShowProducts,
);

export const selectEpicShowProductReferencesState = createSelector(
  selectOpenedListState,
  (state) => state.epicShowProductReferences,
);

export const { selectAll: selectEpicShowProducts } =
  epicShowProductsAdapter.getSelectors(selectEpicShowProductsState);

export const { selectAll: selectEpicShowProductReferences } =
  epicShowProductReferencesAdapter.getSelectors(
    selectEpicShowProductReferencesState,
  );

export const selectOpenedList = createSelector(
  selectOpenedListState,
  (state) => {
    return state.list;
  },
);

export const selectOpenedListQueryParams = createSelector(
  selectOpenedListState,
  (state) => {
    return {
      id: state.list?.id,
      page: state.page,
      size: state.size,
      available: true,
      productReferences: state.list?.productsReference,
    };
  },
);

export const selectOpenedListProductReferences = createSelector(
  selectOpenedListState,
  (state) => state.list?.productsReference ?? [],
);

export const selectOpenedListProductsLoading = createSelector(
  selectOpenedListState,
  (state) => state.loading,
);

export const selectShopTheListLoading = createSelector(
  selectOpenedListState,
  (state) => state.shopTheListLoading,
);

export const selectProductListAmount = createSelector(
  selectOpenedListState,
  (state) => state.list?.amount,
);

/**
 * Returns product list items amount
 */
export const selectTotal = createSelector(
  selectOpenedListState,
  (state) => state.total,
);

export const selectTotalAvailable = createSelector(
  selectOpenedListState,
  (state) => state.totalAvailable,
);

export const selectIsOwnerOfOpenedList = createSelector(
  selectUser,
  selectOpenedList,
  (user, list) => {
    if (!user || !list) return undefined;
    return user.externalId === list.creation.user?.externalId;
  },
);

export const selectIsConsumingClonedEpicShow = createSelector(
  selectOpenedList,
  selectIsSalesRepOrAdmin,
  selectIsOwnerOfOpenedList,
  (list, isAdminOrSalesRep, isOwner) => {
    if (!list) return false;

    const isEpicShow = list.mainMedia?.mediaType === MediaType.Directshow;
    const isCloned = !!list.originalProductList?.id;

    if (isEpicShow && isCloned && isOwner && !isAdminOrSalesRep) return true;
    return false;
  },
);

export const selectTotalProductsByCurrentAvailabilityStatus = createSelector(
  selectTotalAvailable,
  (totalAvailable) => totalAvailable ?? 0,
);

/**
 * Returns true if there are still any products to load in the product list
 */
export const selectIsRemainingProductsNotLoaded = createSelector(
  selectOpenedListProducts,
  selectTotalProductsByCurrentAvailabilityStatus,
  (products, total) => products.length < total,
);

/**
 * Returns if all products are loaded
 */
export const selectOpenedListAllProductsLoaded = createSelector(
  selectIsRemainingProductsNotLoaded,
  selectTotalProductsByCurrentAvailabilityStatus,
  (isRemainingProductsNotLoaded, isAllProductsLoaded) => {
    return !isRemainingProductsNotLoaded && isAllProductsLoaded >= 0;
  },
);

export const selectShowLookBookNameInTitle = selectConfigurationValueFrom<
  ProductListConfigurationKeys.ShowLookBookNameInTitle,
  ProductListConfigurations
>(
  PRODUCT_LIST_NAMESPACE,
  ProductListConfigurationKeys.ShowLookBookNameInTitle,
  false,
);

export const selectShowListGrouping = createSelector(
  selectConfigurationsState,
  (config) => {
    if (!config) return undefined;
    return getValueFromConfigState<
      ProductListConfigurationKeys.ShowGrouping,
      ProductListConfigurations
    >(
      config,
      PRODUCT_LIST_NAMESPACE,
      ProductListConfigurationKeys.ShowGrouping,
      false,
    );
  },
);

export const selectOpenedListIsEpicShow = createSelector(
  selectOpenedList,
  (list) => list?.mainMedia?.mediaType === MediaType.Directshow,
);

export const selectHideFilterGroupInOpenedList = createSelector(
  selectOpenedListIsEpicShow,
  selectIsSalesRepOrAdmin,
  selectIsOwnerOfOpenedList,
  (isEpicShow, isSalesRepOrAdmin, isOwnerOfOpenedList) =>
    isEpicShow && !isSalesRepOrAdmin && !isOwnerOfOpenedList,
);

export const selectAllowExportGrouping = createSelector(
  selectConfigurationsState,
  (config) => {
    if (!config) return undefined;
    return getValueFromConfigState<
      ProductListConfigurationKeys.AllowExportGrouping,
      ProductListConfigurations
    >(
      config,
      PRODUCT_LIST_NAMESPACE,
      ProductListConfigurationKeys.AllowExportGrouping,
      ProductListConfiguration[
        ProductListConfigurationKeys.AllowExportGrouping
      ],
    );
  },
);

export const selectGroupExportProducts = createSelector(
  selectAllowExportGrouping,
  selectListEntitySearchSortType(),
  (allowExportGrouping, searchSortType) => {
    if (!allowExportGrouping) {
      return false;
    }

    return !!searchSortType;
  },
);
